const COMPLETE_POSTCODE_PATTERNS = {
	US: /^\d{5}(-\d{4})?$/,
	CA: /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/,
	MX: /^\d{5}$/,
};

type Postcode = string | null | undefined;

const HAS_POSTCODE_VALUE = (value: Postcode): boolean => String(value || '').trim().length > 0;

/**
 * Whether a postal code is whole rather than a prefix, judged by its country's shape.
 *
 * A country we have no pattern for is taken at its word, so blanks have to be
 * rejected before that fallback gets a say.
 */
export const IS_COMPLETE_POSTCODE = (value: Postcode, country: Postcode): boolean => {
	const postcode = String(value || '')
		.trim()
		.toUpperCase();
	if (!postcode) {
		return false;
	}
	const pattern = COMPLETE_POSTCODE_PATTERNS[String(country || '').trim().toUpperCase()];
	return pattern ? pattern.test(postcode) : true;
};

/**
 * Picks the postal code to use from Google's `postal_code` components, in the order
 * Google returned them.
 *
 * Google truncates some postal codes to their prefix and gives no way to tell: reverse
 * geocoding downtown Montreal puts a bare 'H3B' on the most specific result, typed
 * plainly `postal_code`, while the full 'H3B 1S5' sits further down the list. The
 * `postal_code_prefix` type does not mark them, hence judging by shape instead.
 *
 * Blanks are skipped rather than kept as partials: the first partial wins, so a blank
 * would lock the slot against a real one behind it.
 */
export const SELECT_POSTCODE = (
	candidates: Postcode[],
	country: Postcode
): { complete: string | null; partial: string | null } => {
	let complete: string | null = null;
	let partial: string | null = null;
	for (const candidate of candidates || []) {
		if (!HAS_POSTCODE_VALUE(candidate)) {
			continue;
		}
		if (IS_COMPLETE_POSTCODE(candidate, country)) {
			complete = candidate;
			break;
		}
		if (partial === null) {
			partial = candidate;
		}
	}
	return { complete, partial };
};
